import React, { useState, memo } from 'react';

const SystemPromptEditor = memo(({ 
  customSystemPrompt, 
  onSystemPromptChange, 
  sourceLanguage, 
  targetLanguage, 
  supportedLanguages,
  isRecording 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tempPrompt, setTempPrompt] = useState(customSystemPrompt || '');

  const sourceLangName = supportedLanguages[sourceLanguage]?.name || 'English';
  const targetLangName = supportedLanguages[targetLanguage]?.name || 'English';

  // Process placeholders in the prompt
  const processPrompt = (prompt) => {
    if (!prompt) return prompt;
    return prompt
      .replace(/\{SOURCE_LANGUAGE\}/g, sourceLangName)
      .replace(/\{TARGET_LANGUAGE\}/g, targetLangName);
  };

  const defaultPrompt = `You are a professional translator. Translate the following text from {SOURCE_LANGUAGE} to {TARGET_LANGUAGE}. Provide only the translation, maintaining accuracy, context, and natural language flow. Do not include explanations or additional text.`;

  const handleSave = () => {
    onSystemPromptChange(tempPrompt.trim() || null);
    setIsExpanded(false);
  };

  const handleReset = () => {
    setTempPrompt('');
    onSystemPromptChange(null);
  };

  const handleCancel = () => {
    setTempPrompt(customSystemPrompt || '');
    setIsExpanded(false);
  };

  const presetPrompts = [
    {
      name: "Professional Translation",
      prompt: `You are a professional translator. Translate the following text from {SOURCE_LANGUAGE} to {TARGET_LANGUAGE}. Provide only the translation, maintaining accuracy, context, and natural language flow. Do not include explanations or additional text.`
    },
    {
      name: "Casual/Conversational",
      prompt: `You are a friendly translator. Translate the following text from {SOURCE_LANGUAGE} to {TARGET_LANGUAGE} in a casual, conversational style. Make it sound natural and approachable.`
    },
    {
      name: "Formal/Business",
      prompt: `You are a business translator. Translate the following text from {SOURCE_LANGUAGE} to {TARGET_LANGUAGE} using formal, professional language appropriate for business communications.`
    },
    {
      name: "Technical/Academic",
      prompt: `You are a technical translator. Translate the following text from {SOURCE_LANGUAGE} to {TARGET_LANGUAGE} maintaining technical accuracy and academic precision. Preserve technical terms and concepts.`
    },
    {
      name: "Creative/Literary",
      prompt: `You are a creative translator. Translate the following text from {SOURCE_LANGUAGE} to {TARGET_LANGUAGE} preserving the creative style, tone, and artistic expression of the original text.`
    }
  ];

  return (
    <div className="system-prompt-editor" style={{
      backgroundColor: 'transparent', // Remove background to integrate better
      border: 'none', // Remove border to integrate better
      borderRadius: '0px',
      padding: '0px', // Remove padding to integrate better
      margin: '0px' // Remove margin to integrate better
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ margin: 0, color: '#495057', fontSize: '14px', fontWeight: '500' }}>
          🎯 Custom System Prompt
          {customSystemPrompt && (
            <span style={{ 
              marginLeft: '10px', 
              fontSize: '12px', 
              backgroundColor: '#28a745', 
              color: 'white', 
              padding: '2px 8px', 
              borderRadius: '12px' 
            }}>
              CUSTOM
            </span>
          )}
        </span>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={isRecording}
          style={{
            backgroundColor: isExpanded ? '#dc3545' : '#007bff',
            color: 'white',
            border: 'none',
            padding: '5px 12px',
            borderRadius: '4px',
            cursor: isRecording ? 'not-allowed' : 'pointer',
            fontSize: '12px'
          }}
        >
          {isExpanded ? '✕ Close' : '✏️ Edit'}
        </button>
      </div>

      {/* Current Prompt Preview */}
      <div style={{
        backgroundColor: '#e9ecef',
        padding: '10px',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#495057',
        fontStyle: customSystemPrompt ? 'normal' : 'italic',
        marginBottom: isExpanded ? '15px' : '0'
      }}>
        <strong>Current:</strong> {processPrompt(customSystemPrompt) || processPrompt(defaultPrompt)}
      </div>

      {/* Placeholder Info */}
      {isExpanded && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          padding: '8px',
          fontSize: '11px',
          color: '#856404',
          marginBottom: '10px'
        }}>
          <strong>💡 Dynamic Placeholders:</strong><br/>
          • <code>{'{SOURCE_LANGUAGE}'}</code> → {sourceLangName}<br/>
          • <code>{'{TARGET_LANGUAGE}'}</code> → {targetLangName}
        </div>
      )}

      {/* Expanded Editor */}
      {isExpanded && (
        <div style={{ marginTop: '15px' }}>
          {/* Preset Prompts */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              📋 Preset Prompts:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
              {presetPrompts.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => setTempPrompt(preset.prompt)}
                  style={{
                    backgroundColor: '#e9ecef',
                    border: '1px solid #ced4da',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textAlign: 'left'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#dee2e6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#e9ecef'}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Prompt Textarea */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              ✏️ Edit Prompt:
            </label>
            <textarea
              id="system-prompt"
              name="system-prompt"
              value={tempPrompt}
              onChange={(e) => setTempPrompt(e.target.value)}
              placeholder={`Enter your custom system prompt here...\n\nExample:\n${defaultPrompt}`}
              rows="6"
              style={{
                width: 'calc(100% - 24px)', // Account for padding to prevent overflow
                padding: '12px',
                border: '2px solid #ced4da',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '120px',
                boxSizing: 'border-box' // Include padding in width calculation
              }}
            />
            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '5px' }}>
              💡 Tip: The system prompt defines how the AI should behave. Be specific about the translation style, tone, and requirements you want.
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={handleReset}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              🔄 Reset to Default
            </button>
            <button
              onClick={handleCancel}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ✕ Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              ✅ Save & Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default SystemPromptEditor;
